import { installMriBridge } from "./mri-bridge";
import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";
import VueSkeletonLoader from "skeleton-loader-vue";
import VueWaypoint from "vue-waypoint";
import VueClipboard from "vue-clipboard2";
import mixpanel from "mixpanel-browser";
import TextareaAutosize from "vue-textarea-autosize";
import bridgeLoginApp from "./plugins/bridgeApp";
import ibkApp from "./plugins/ibkApp";
import infiniteScroll from "vue-infinite-scroll";
import Meta from "vue-meta";
import inicisApp from "./plugins/inicisApp";
import paypleApp from "./plugins/paypleApp";
import userExperimentApp from "./plugins/userExperimentApp";
import flagApp from "./plugins/flagApp";
import * as Sentry from "@sentry/vue";
import sentryMessage from "./plugins/sentryLogApp";
import deviceApp from "./plugins/deviceApp";
import { initAnalytics } from "./analytics";
import { initTracker } from "@/utils/mixpanel-tracker";
import { isInMirrorCohort } from "@/utils/shareTracker";
import { enqueueBehavior, initBehaviorQueue } from "@/utils/behaviorQueue";
import { recordJsError } from "@/utils/errorContext";
import { installPgResultProbe } from "@/utils/pgResultProbe";

// P0-2(NZSC-3208) 전량 행동데이터 미러 — $track_*/$mixpanel_track 발사와 나란히 자체수집(배치 큐 →
// SQS-only, bridge 스킵). 2026-06-23~30 10%에서 7일간 에러0·끊김0·일~3000건(부하 무시가능) 검증
// → 100% 전환(전 고객 자체수집). 유저 코호트 단위. 큐 push 만이라 비차단·절대 throw 안 함.
const BEHAVIOR_MIRROR_PCT = 100;
const IS_MIRROR_COHORT = (() => {
  try {
    return isInMirrorCohort(BEHAVIOR_MIRROR_PCT);
  } catch {
    return false;
  }
})();
if (IS_MIRROR_COHORT) initBehaviorQueue(); // 재시도 드레인 + 페이지이탈 flush 리스너 1회
// 선별 dual 이벤트는 trackUserEvent(shareTracker)가 이미 SQS로 보냄 — 미러까지 보내면
// track_events 에 2번 적재(실측 self≈2×bridge). 미러에서 스킵해 1건만 남긴다.
const DUAL_FIRED_SELECTIVE = new Set(["view_report_apply_step", "select_option"]);
function behaviorMirror(eventName, properties) {
  if (!IS_MIRROR_COHORT) return;
  if (DUAL_FIRED_SELECTIVE.has(eventName)) return;
  enqueueBehavior(eventName, properties);
}

Vue.config.productionTip = false;

// NZSC-4015: 오류 페이지 원인 한 줄 보고용 — 마지막 JS 예외·PG 결제창 결과를 관문에서 기록한다.
// 예외를 삼키지 않는다(기존 console 출력 유지). 기록 실패도 화면을 막지 않는다.
Vue.config.errorHandler = (err, vm, info) => {
  try {
    recordJsError({ message: err?.message || String(err), source: `vue:${info || ""}` });
  } catch (e) {
    /* noop */
  }
  window.console.error(err);
};
window.addEventListener("error", e => {
  try {
    recordJsError({
      message: e.message || String(e.error || "error"),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
    });
  } catch (err) {
    /* noop */
  }
});
window.addEventListener("unhandledrejection", e => {
  try {
    const r = e.reason;
    recordJsError({
      message: typeof r === "string" ? r : r?.message || "unhandledrejection",
      source: r && typeof r === "object" && r.stack ? String(r.stack).split("\n")[1]?.trim() : undefined,
    });
  } catch (err) {
    /* noop */
  }
});
installPgResultProbe();
Vue.component("vue-skeleton-loader", VueSkeletonLoader);
Vue.use(VueWaypoint);
Vue.use(bridgeLoginApp);
Vue.use(VueClipboard);
Vue.use(TextareaAutosize);
Vue.use(infiniteScroll);
Vue.use(ibkApp);
Vue.use(Meta, {
  attribute: "data-vue-meta",
});
Vue.use(inicisApp, {
  impId: "imp05687488",
});
Vue.use(paypleApp);

// Mixpanel 플랫폼 판별 (common.css 브레이크포인트 1080px 기준)
function getPlatformType() {
  return window.innerWidth <= 1080 ? "mobile_web" : "pc_web";
}

// Mixpanel 초기화 (index.html 스크립트가 주석 처리되어도 mixpanel-browser 패키지로 작동)
let mixpanelReady = false;
try {
  if (mixpanel && typeof mixpanel.init === "function") {
    mixpanel.init(process.env.VUE_APP_MIXPANEL_TOKEN, {
      debug: true,
      track_pageview: true,
      // ignore_dnt: true,
    });
    mixpanelReady = true;

    // Super properties — 모든 이벤트에 자동 첨부
    mixpanel.register({
      platform: getPlatformType(),
      app_version: process.env.VUE_APP_VERSION || "unknown",
      viewport: window.innerWidth + "x" + window.innerHeight,
    });

    // 다방 인입 세션(NZSC-3205): 모든 이벤트에 channel=dabang 자동 첨부 — 파트너 세그먼트 분석용.
    // sessionStorage 기준 부팅마다 동기화(웹뷰 밖/일반 진입은 unregister) — 일반 유저 오염 방지.
    try {
      if (window.sessionStorage.getItem("dabang_entered") === "1") {
        mixpanel.register({ channel: "dabang" });
      } else {
        mixpanel.unregister("channel");
      }
    } catch (e) {
      /* 세션스토리지 접근 실패 무시 */
    }

    // resize 시 platform 갱신 (throttle 1초)
    var resizeTimer = null;
    window.addEventListener(
      "resize",
      function () {
        if (resizeTimer) return;
        resizeTimer = setTimeout(function () {
          resizeTimer = null;
          mixpanel.register({ platform: getPlatformType() });
        }, 1000);
      },
      { passive: true },
    );

    // Phase 2: 자동 행동 이벤트 초기화 (수동적 관찰만, 서비스 영향 없음)
    try {
      initAnalytics(mixpanel, router);
    } catch (e) {
      console.warn("[Analytics] init failed:", e);
    }

    Vue.prototype.$mixpanel = mixpanel;
    // DEPRECATED: 152개 개별 이벤트명 방식. 신규 코드는 $track_view / $track_click / $track_exit 사용.
    // 기존 사용처는 회귀 위험 최소화를 위해 유지하지만 새 발화는 추가하지 마라 (CR에서 반려됨).
    Vue.prototype.$mixpanel_track = (event_name, properties = {}) => {
      if (mixpanelReady && typeof mixpanel.track === "function") {
        mixpanel.track(event_name, properties);
      } else {
        console.warn("[Mixpanel] not initialized — event dropped:", event_name);
      }
      behaviorMirror(event_name, properties); // 전량 미러(Mixpanel 독립 — 준비 안 돼도 자체수집)
    };

    // NZSC-3077 직거래 1차 사이클: 화면 추적 매니저 초기화.
    // 기획서(믹스패널_세팅_최종계획.xlsx) 데이터 모델 = 이벤트 3개 + 속성 7개.
    const tracker = initTracker(mixpanel);
    tracker.mirror = behaviorMirror; // exit_screen 자체수집 미러(발사 지점 = tracker 내부)
    Vue.prototype.$tracker = tracker;
    Vue.prototype.$track_view = function (screen_name, element_type, properties = {}) {
      const eventProperties = {
        ...properties,
        screen_name,
        element_type: element_type || "page",
      };
      tracker.trackView(screen_name, element_type || "page", properties);
      behaviorMirror("view_page", eventProperties);
    };
    Vue.prototype.$track_click = function (action_target, properties = {}) {
      const eventProperties = { ...properties, action_target };
      tracker.trackClick(action_target, properties);
      behaviorMirror("click_action", eventProperties);
    };
    Vue.prototype.$track_exit = function () {
      tracker.trackExit();
    };
  } else {
    // mixpanel이 없을 때 빈 함수로 대체 (에러 방지)
    Vue.prototype.$mixpanel = {
      track: () => {},
      identify: () => {},
      people: { set: () => {} },
    };
    Vue.prototype.$mixpanel_track = event_name => {
      console.warn("[Mixpanel] not initialized — event dropped:", event_name);
    };
    Vue.prototype.$track_view = () => {};
    Vue.prototype.$track_click = () => {};
    Vue.prototype.$track_exit = () => {};
  }
} catch (e) {
  console.warn("Mixpanel initialization failed:", e);
  // 에러 발생 시 빈 함수로 대체
  Vue.prototype.$mixpanel = {
    track: () => {},
    identify: () => {},
    people: { set: () => {} },
  };
  Vue.prototype.$mixpanel_track = event_name => {
    console.warn("[Mixpanel] not initialized — event dropped:", event_name);
  };
  Vue.prototype.$track_view = () => {};
  Vue.prototype.$track_click = () => {};
  Vue.prototype.$track_exit = () => {};
}

// 라우트 변경 시 super properties 갱신
// MRI 브릿지 — Preview env(VUE_APP_MRI_BRIDGE=1)에서만 동작, 프로덕션은 no-op (NZSC-3867)
installMriBridge(router);

router.afterEach((to, from) => {
  if (mixpanelReady) {
    mixpanel.register({
      route_name: to.name || "",
      route_path: to.path,
      referrer_path: from.fullPath,
    });
  }
  // 자체수집 자동 페이지뷰 — $track_view 없는 화면(결제완료 등)도 "화면 열림"을 100% 커버.
  // Mixpanel 자동캡처($mp_web_page_view)의 자체수집판. 실측: 컷오버 후 완료 페이지 방문이
  // 로직 이벤트(Purchase)만 남아 X-Ray/브리핑 방문 집계가 과소되던 갭의 근본 해결.
  behaviorMirror("page_view_auto", { route_name: to.name || "", referrer_path: from.fullPath });
});

// ── 히트맵 좌표용 스크롤 컨테이너 인식 ──
// 이 앱은 window/documentElement 가 아니라 내부 컨테이너(예: <article class="main">)가 스크롤된다.
// 따라서 실제 스크롤러 기준으로 좌표를 계산해야 "문서 절대 위치"가 기록된다.
// (window.scrollY 로 계산하면 항상 0 → "그때 보이던 뷰포트 %"만 남아 스크롤 정보가 소실됨)
function _isScrollable(el) {
  if (!el || el.nodeType !== 1) return false;
  const s = window.getComputedStyle(el);
  return (
    (s.overflowY === "auto" || s.overflowY === "scroll") && el.scrollHeight > el.clientHeight + 4
  );
}
function _scrollerOf(node) {
  let el = node;
  while (el && el.nodeType === 1 && el !== document.body && el !== document.documentElement) {
    if (_isScrollable(el)) return el;
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}
function _isDocScroller(sc) {
  return (
    sc === document.scrollingElement || sc === document.documentElement || sc === document.body
  );
}

// 자체수집 자동 클릭(click_auto) — 트래킹 코드가 안 박힌 버튼·링크의 클릭도 100% 수집.
// Mixpanel 자동캡처(Auto Element Clicked)의 자체수집판 = "모든 개인 행위" 커버 완성.
// 대상: 인터랙티브 요소(버튼/링크/role/onclick/pointer 커서)만 — 빈 화면 클릭 노이즈 제외.
// ⚠️ 입력값(PII) 절대 수집 금지: input/textarea/select 는 텍스트 대신 name/placeholder 만.
document.addEventListener(
  "click",
  e => {
    try {
      // 히트맵 좌표 — 실제 스크롤러 기준. 세로 = 문서(스크롤러) 전체 %, 가로 = 스크롤러 폭 %
      const sc = _scrollerOf(e.target);
      const dr = _isDocScroller(sc) ? { top: 0, left: 0 } : sc.getBoundingClientRect();
      const docH = Math.max(1, sc.scrollHeight);
      const scW = sc.clientWidth || window.innerWidth;
      const yInDoc = e.clientY - dr.top + (sc.scrollTop || 0);
      const xPct = Math.round(((e.clientX - dr.left) / Math.max(1, scW)) * 1000) / 10;
      const yDocPct = Math.round((yInDoc / docH) * 1000) / 10;
      const coord = { x_pct: xPct, y_doc_pct: yDocPct, vw: scW, doc_h: docH };

      let el = e.target;
      for (let depth = 0; el && depth < 6; depth++, el = el.parentElement) {
        const tag = (el.tagName || "").toLowerCase();
        const interactive =
          tag === "button" ||
          tag === "a" ||
          el.getAttribute?.("role") === "button" ||
          el.onclick ||
          (el.dataset && el.dataset.xray) ||
          (tag === "input" && ["button", "submit", "checkbox", "radio"].includes(el.type)) ||
          tag === "select" ||
          tag === "label" ||
          (window.getComputedStyle && depth === 0 && getComputedStyle(el).cursor === "pointer");
        if (!interactive) continue;
        const isField = ["input", "textarea", "select"].includes(tag);
        const text = isField
          ? el.name || el.placeholder || el.type || ""
          : (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 40);
        behaviorMirror("click_auto", {
          tag,
          text,
          class_name: String(el.className || "").slice(0, 60),
          interactive: "1",
          ...coord,
        });
        return;
      }
      // 인터랙티브 조상 없음 = dead click 후보 — 히트맵·UX 마찰 분석용 (요소 텍스트는 수집 안 함)
      const t = (e.target && e.target.tagName ? e.target.tagName : "").toLowerCase();
      behaviorMirror("click_auto", { tag: t, text: "", interactive: "0", ...coord });
    } catch (err) {
      // 자동 클릭 수집 실패가 서비스에 영향 주지 않음
    }
  },
  true,
);

// 어텐션 히트맵(scroll_map) — 문서 세로 10구간별 노출 시간(초)을 화면 이탈 시 1건으로 전송.
// "사람들이 실제로 오래 본 영역"의 원천. 볼륨 = 페이지뷰당 1건.
(() => {
  let buckets = new Array(10).fill(0);
  let lastT = Date.now();
  let lastPath = window.location.pathname;
  let curScroller = null; // 마지막으로 스크롤된 컨테이너 (document 캡처 리스너가 갱신)
  function currentScroller() {
    if (
      curScroller &&
      curScroller.isConnected &&
      curScroller.scrollHeight > curScroller.clientHeight + 4
    ) {
      return curScroller;
    }
    const se = document.scrollingElement || document.documentElement;
    if (se.scrollHeight > se.clientHeight + 4) return se;
    // fallback: 가장 큰 스크롤 요소 (스크롤 전 초기 flush 대비 — 드물어서 비용 무시)
    let best = se;
    let bh = se.scrollHeight;
    document.querySelectorAll("*").forEach(el => {
      const s = window.getComputedStyle(el);
      if (
        (s.overflowY === "auto" || s.overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight + 4 &&
        el.scrollHeight > bh
      ) {
        bh = el.scrollHeight;
        best = el;
      }
    });
    return best;
  }
  function visibleBuckets() {
    const sc = currentScroller();
    const docH = Math.max(1, sc.scrollHeight);
    const st = sc.scrollTop || 0;
    const vh = sc.clientHeight || window.innerHeight;
    const top = st / docH;
    const bot = Math.min(1, (st + vh) / docH);
    const out = [];
    for (let b = 0; b < 10; b++) {
      if (b / 10 < bot && (b + 1) / 10 > top) out.push(b);
    }
    return out;
  }
  function accrue() {
    const now = Date.now();
    const dt = Math.min(now - lastT, 15000); // 방치 캡
    if (dt > 50 && document.visibilityState === "visible") {
      for (const b of visibleBuckets()) buckets[b] += dt;
    }
    lastT = now;
  }
  function flushScrollMap() {
    accrue();
    const total = buckets.reduce((a, x) => a + x, 0);
    if (total >= 1000) {
      const props = { path: lastPath, total_sec: Math.round(total / 1000) };
      buckets.forEach((ms, i) => (props["b" + i] = Math.round(ms / 1000)));
      behaviorMirror("scroll_map", props);
    }
    buckets = new Array(10).fill(0);
    lastT = Date.now();
    lastPath = window.location.pathname;
  }
  let scrollTh = null;
  // 이 앱은 내부 컨테이너가 스크롤되므로 window 'scroll' 은 안 뜬다 → document 캡처로 모든 스크롤 포착.
  document.addEventListener(
    "scroll",
    e => {
      const tg = e.target;
      if (tg && tg.nodeType === 1 && tg.scrollHeight > tg.clientHeight + 4) curScroller = tg;
      if (scrollTh) return;
      scrollTh = setTimeout(() => {
        scrollTh = null;
        accrue();
      }, 500);
    },
    { capture: true, passive: true },
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushScrollMap();
    else lastT = Date.now();
  });
  window.addEventListener("pagehide", flushScrollMap);
  router.afterEach(() => flushScrollMap());
})();

Vue.prototype.$payple_pay = PaypleCpayAuthCheck;

Vue.use(userExperimentApp, {
  store,
  router,
  options: {
    defaultAutoRedirect: true,
  },
});

// NZSC-3307: 실험 플래그($flagPrefetch / $flag). 값 기반이라 실험 종료에 배포가 필요 없다.
// NZSC-3582: mirror 주입 — $flag 해석 시점에 자체수집 공통 노출 이벤트(experiment_exposure)
// 를 behaviorMirror 로 발화한다(세션당 실험별 1회, Mixpanel 은 super property 가 커버).
Vue.use(flagApp, { store, mirror: behaviorMirror });

Vue.use(deviceApp);

Sentry.init({
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  integrations: [],
  tracesSampleRate: 0.0,
  beforeSend(event, hint) {
    if (event.tags?.manual) {
      return event;
    }
    return null;
  },
});
Vue.use(sentryMessage);

var vm = new Vue({
  router,
  store,
  axios,
  bridgeLoginApp,
  render: h => h(App),
});

window.app = vm.$mount("#app");
